import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { MapPin, Camera, Waveform, FilePdf, CheckCircle, ShieldWarning, Buildings, ArrowsOut } from "@phosphor-icons/react";
import PhotoCarousel from "@/components/PhotoCarousel";

const STATUS_META = {
  waiting: { label: "En attente", color: "#9CA3AF" },
  in_progress: { label: "En cours", color: "#005CFF" },
  urgent: { label: "En cours urgent", color: "#EF4444" },
  done: { label: "Terminé", color: "#10B981" },
};

/**
 * Public tokenized page — /public/chantier/:token
 * No authentication needed. Whitelisted data only.
 */
export default function PublicChantier() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [carouselOpen, setCarouselOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/chantiers/${token}`);
        setData(r.data);
      } catch (err) {
        setError(err?.response?.data?.detail || "Lien invalide ou révoqué");
      }
    })();
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6" data-testid="public-error">
        <div className="max-w-sm text-center">
          <ShieldWarning size={40} className="text-amber-400 mx-auto mb-3" weight="duotone" />
          <div className="font-display font-semibold text-xl mb-1">Accès impossible</div>
          <div className="text-sm text-white/60">{error}</div>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-[#005CFF] rounded-full animate-spin" />
      </div>
    );
  }

  const ch = data.chantier || {};
  const status = STATUS_META[ch.status] || STATUS_META.waiting;
  const client = data.client || {};
  const tasksWithPhotos = (data.tasks || []).filter((t) => (t.photos_before || []).length > 0 || (t.photos_after || []).length > 0);
  const debriefs = data.debriefs || [];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="public-chantier-page">
      {/* Header */}
      <div className="border-b border-white/10 bg-gradient-to-b from-[#111] to-[#0A0A0A]">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/40 mb-3">
            <Buildings size={11} weight="duotone" /> BKPRO Électricité · Suivi client
          </div>
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <h1 className="font-display font-bold text-2xl sm:text-3xl leading-tight" data-testid="public-chantier-name">{ch.name}</h1>
              {ch.address && (
                <div className="text-sm text-white/60 mt-1 flex items-center gap-1.5"><MapPin size={12} weight="duotone" /> {ch.address}</div>
              )}
              {client.name && <div className="text-xs text-white/40 mt-1">Client : {client.name}</div>}
            </div>
            <span className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full shrink-0" style={{ background: status.color + "22", color: status.color, border: `1px solid ${status.color}55` }} data-testid="public-status">
              {status.label}
            </span>
          </div>

          {typeof ch.progress === "number" && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-[10px] text-white/40 mb-1">
                <span>Avancement</span>
                <span className="font-mono">{ch.progress}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${ch.progress}%`, background: "linear-gradient(90deg,#005CFF,#7CB2FF)" }} />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Summary */}
        {(ch.client_summary || ch.description) && (
          <section data-testid="public-summary">
            <div className="text-[11px] uppercase tracking-widest text-white/40 mb-2">Résumé</div>
            <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 text-sm text-white/85 whitespace-pre-wrap leading-relaxed">
              {ch.client_summary || ch.description}
            </div>
          </section>
        )}

        {/* Photos before/after */}
        {tasksWithPhotos.length > 0 && (
          <section data-testid="public-photos">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-white/40">
                <Camera size={11} weight="duotone" /> Photos avant / après
              </div>
              <button
                onClick={() => setCarouselOpen(true)}
                className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-[#005CFF]/15 border border-[#005CFF]/30 text-[#7CB2FF] hover:bg-[#005CFF]/25 active:scale-95"
                data-testid="open-carousel-btn"
              >
                <ArrowsOut size={11} weight="bold" /> Vue plein écran
              </button>
            </div>
            <div className="space-y-4">
              {tasksWithPhotos.map((t) => (
                <div key={t.id} className="rounded-2xl bg-white/[0.03] border border-white/10 p-3 space-y-2" data-testid={`public-task-${t.id}`}>
                  <div className="flex items-center gap-2">
                    {t.done && <CheckCircle size={14} className="text-emerald-400" weight="fill" />}
                    <div className={`text-sm ${t.done ? "line-through text-white/50" : "text-white"}`}>{t.label}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <PhotoColumn label="Avant" tone="amber" token={token} taskId={t.id} slot="before" photos={t.photos_before} />
                    <PhotoColumn label="Après" tone="emerald" token={token} taskId={t.id} slot="after" photos={t.photos_after} />
                  </div>
                </div>
              ))}
            </div>
            <PhotoCarousel open={carouselOpen} onClose={() => setCarouselOpen(false)} token={token} tasks={tasksWithPhotos} />
          </section>
        )}

        {/* Debriefs */}
        {debriefs.length > 0 && (
          <section data-testid="public-debriefs">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-white/40 mb-2">
              <Waveform size={11} weight="duotone" /> Journal d&apos;intervention
            </div>
            <div className="space-y-2">
              {debriefs.map((d) => (
                <div key={d.id} className="rounded-2xl bg-white/[0.03] border border-white/10 p-3" data-testid={`public-debrief-${d.id}`}>
                  <div className="text-[10px] text-white/40 font-mono mb-1">
                    {(d.created_at || "").slice(0, 16).replace("T", " ")} · {d.user_name}
                  </div>
                  {d.transcription && <div className="text-sm text-white/85 whitespace-pre-wrap leading-relaxed">{d.transcription}</div>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Signed PDF */}
        {data.has_report && (
          <section data-testid="public-pdf">
            <a
              href={`${API}/public/chantiers/${token}/report-pdf`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 rounded-2xl bg-[#005CFF] hover:bg-[#0047CC] transition-colors text-white font-semibold py-4 active:scale-[0.99]"
              data-testid="public-pdf-btn"
            >
              <FilePdf size={18} weight="duotone" /> Télécharger le rapport signé (PDF)
            </a>
          </section>
        )}

        {/* Empty state */}
        {tasksWithPhotos.length === 0 && debriefs.length === 0 && !data.has_report && (
          <div className="text-center py-10 text-white/40 text-sm" data-testid="public-empty">
            L&apos;équipe n&apos;a pas encore publié de contenu pour ce chantier.
            <div className="mt-1 text-[11px]">Revenez plus tard — les photos et rapports arriveront ici.</div>
          </div>
        )}

        <div className="text-center text-[10px] text-white/25 pt-6 pb-4">
          Fourni par BKflow · lien privé
        </div>
      </div>
    </div>
  );
}

function PhotoColumn({ label, tone, token, taskId, slot, photos }) {
  const color = tone === "amber" ? "#F59E0B" : "#10B981";
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest mb-1" style={{ color }}>{label}</div>
      {(!photos || photos.length === 0) ? (
        <div className="aspect-square rounded-md border border-dashed border-white/10 flex items-center justify-center text-white/25 text-xs">—</div>
      ) : (
        <div className="grid grid-cols-2 gap-1">
          {photos.map((p, i) => (
            <a key={p.id} href={`${API}/public/chantiers/${token}/photo/${taskId}/${slot}/${p.id}`} target="_blank" rel="noreferrer" className="aspect-square rounded-md overflow-hidden border border-white/10 hover:border-white/30 transition-colors">
              <img src={`${API}/public/chantiers/${token}/photo/${taskId}/${slot}/${p.id}`} alt={`${label} ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
